import { Box, Divider, Typography } from '@mui/material';
import { PartnerReferralActivity, Student } from '@uplift/api';
import { useGetStudentByStudentId, useHigherEdOrgQueryView } from '@uplift/queries';
import { CalendarDateService } from '@uplift/services';
import { SubSystem, User } from '@uplift/types';
import { formatReferrerNameWithCredentials, getCareTypeLabel } from '@uplift/utils';
import { ReferralDetailsSection } from './ReferralDetails.component';
import { usePartnerReferralDetailsStyles } from '../styles';
import { NoteDetails } from './NoteDetails.component';

export type PartnerReferralDetailsProps = {
  activityItem: PartnerReferralActivity;
};

export const PartnerReferralDetails: React.VFC<PartnerReferralDetailsProps> = ({ activityItem }) => {
  const { classes } = usePartnerReferralDetailsStyles();

  const studentId = activityItem?.aboutUser?.user?.id;
  const { data: student } = useGetStudentByStudentId(studentId, { enabled: !!studentId, suspense: true });
  const orgId = (student as Student).orgId;
  const { data: org } = useHigherEdOrgQueryView(orgId, { enabled: !!orgId, suspense: true });
  const orgName = org?.name;
  const isPartnerReferral = activityItem?.fromUser?.user?.subsystem === SubSystem.higherEd;
  const referralDate = CalendarDateService.date(activityItem.datetime).tz(CalendarDateService.timezones.ET);
  const referrerNameWithCredentials = formatReferrerNameWithCredentials(
    activityItem.fromUser?.user as User,
    activityItem.fromUser?.profile
  );

  return (
    <Box className={classes.content}>
      <Box display="flex" flexDirection="column" gap="8px">
        <Typography variant="overline" className={classes.subTitle}>
          Care Type
        </Typography>
        <Typography variant="body1" data-testid="referral-care-type">
          {getCareTypeLabel(activityItem.referral.careType)}
        </Typography>
      </Box>
      <Divider />
      <Box display="flex" flexDirection="column" gap="8px">
        <Typography variant="overline" className={classes.subTitle}>
          Note to Provider
        </Typography>
        <NoteDetails content={activityItem.referral.content} />
        <Box>
          {isPartnerReferral && (
            <Typography variant="body1" data-testid="referral-from">
              <span className={classes.weightBold}>From: </span>
              {referrerNameWithCredentials}
            </Typography>
          )}
          <Typography variant="body1" data-testid="referral-contact">
            <span className={classes.weightBold}>Contact: </span>
            {activityItem.fromUser?.user?.email}
          </Typography>
        </Box>
      </Box>
      <Divider />
      <Box display="flex" flexDirection="column" gap="8px">
        <ReferralDetailsSection
          referralDate={referralDate}
          subtitle={`Referral to UpLift from ${orgName} partnership`}
        />
      </Box>
    </Box>
  );
};
